import {validateSchedule} from '@cap4/cap4publish-domain';

export type IntentStorage=Pick<Storage,'getItem'|'setItem'|'removeItem'>;
export type PublicationCommand={operation:string;input:unknown};
export type PublicationOperationReceipt<TJob=unknown>={result:unknown;job:TJob|null};
type PersistedIntent={version:1;userId:string;resourceId:string;command:PublicationCommand};
type Send=(operation:string,input:unknown)=>Promise<unknown>;
const operations=new Set(['publishNow','schedulePublication','reschedulePublication','cancelPublication','replaceScheduledRevision','resolveUnknown','retryPublication']);
const fields=new Set(['resourceId','target','revisionId','reviewedRevisionId','operationId','jobId','attemptId','expectedJobVersion','expectedDraftVersion','sourceDraftVersion','expectedUpdatedAt','runAt','timeZone','timing','resolution']);
const targetFields=new Set(['kind','variantId','articleId','connectionId','expectedPublishedRevisionId']);
const resolutionFields=new Set(['kind','providerId','providerUrl']);
const definiteRejections=new Set(['forbidden','invalid','conflict','unsupported','not_found']);

function record(value:unknown):value is Record<string,unknown>{return !!value&&typeof value==='object'&&!Array.isArray(value)}
function safeFields(value:unknown,allowed:Set<string>):boolean{
 return record(value)&&Object.entries(value).every(([key,item])=>allowed.has(key)&&(item===null||typeof item==='string'||typeof item==='number'&&Number.isFinite(item)||typeof item==='boolean'));
}
function checkedCommand(value:unknown,resourceId:string):PublicationCommand{
 if(!record(value)||!operations.has(String(value.operation))||!record(value.input))throw new Error('Publiceringshandlingens parametre kan ikke genoptages sikkert.');
 const {target,resolution,...input}=value.input;
 if(!safeFields(input,fields)||typeof input.operationId!=='string'||!input.operationId||input.resourceId!==undefined&&input.resourceId!==resourceId||target!==undefined&&!safeFields(target,targetFields)||resolution!==undefined&&(!safeFields(resolution,resolutionFields)||!record(resolution)||resolution.kind!=='attach'))throw new Error('Publiceringshandlingens parametre kan ikke gemmes sikkert.');
 if(value.operation==='retryPublication'){
  if(input.timing==='scheduled'){
   if(typeof input.runAt!=='string'||typeof input.timeZone!=='string')throw new Error('Planlagt gentagelse kræver tidspunkt og tidszone.');
   validateSchedule(input.runAt,input.timeZone);
  }else if(input.timing!=='now'||input.runAt!==undefined||input.timeZone!==undefined)throw new Error('Gentagelsens tidspunkt kan ikke gemmes sikkert.');
 }
 return structuredClone(value) as PublicationCommand;
}

/** Only command identifiers and timing are persisted, never content, credentials or media bytes. */
export class DurablePublicationIntent {
 readonly key:string;
 private readonly legacyKey:string;
 private attempt?:PersistedIntent;
 private problem='';
 private running=false;
 private epoch=0;
 constructor(private readonly config:{userId:string;resourceId:string;storage:IntentStorage}){
  this.key=`cap4publish:publication-intent:v1:${encodeURIComponent(config.userId)}:${encodeURIComponent(config.resourceId)}`;
  this.legacyKey=`publisher:publication-intent:v1:${encodeURIComponent(config.userId)}:${encodeURIComponent(config.resourceId)}`;
  this.refresh();
 }
 get pending(){return !!this.attempt||!!this.problem}
 /** A session change invalidates in-flight responses, but preserves the original owner's reference. */
 reset(){this.epoch++}
 refresh(){
  this.problem='';
  if(!this.config.userId||!this.config.resourceId){this.problem='Log ind igen for at afklare publiceringshandlingen.';return}
  try{
   const current=this.config.storage.getItem(this.key),legacy=this.config.storage.getItem(this.legacyKey);
   if(current!==null&&legacy!==null&&current!==legacy)throw new Error('Conflicting retained operations');
   const stored=current??legacy;
   if(stored===null){this.attempt=undefined;return}
   const parsed:unknown=JSON.parse(stored);
   if(!record(parsed)||parsed.version!==1||parsed.userId!==this.config.userId||parsed.resourceId!==this.config.resourceId)throw new Error('Invalid scope');
   this.attempt={version:1,userId:this.config.userId,resourceId:this.config.resourceId,command:checkedCommand(parsed.command,this.config.resourceId)};
   if(legacy!==null){
    // Keep the same operation identifier across the name change. Only remove
    // the legacy value after the new copy is durable and both are unchanged.
    this.config.storage.setItem(this.key,stored);
    if(this.config.storage.getItem(this.key)!==stored||this.config.storage.getItem(this.legacyKey)!==legacy)throw new Error('Changed retained operation');
    this.config.storage.removeItem(this.legacyKey);
   }
  }catch{this.problem='Den gemte publiceringshandling kan ikke læses sikkert. Handlingen er låst, indtil browserlageret er tilgængeligt igen.'}
 }
 private assertCurrent(epoch:number){if(epoch!==this.epoch)throw new Error('Adgangen er ændret. Hent indholdet igen.')}
 private clear(attempt:PersistedIntent){
  const stored=this.config.storage.getItem(this.key);
  if(stored!==JSON.stringify(attempt))throw new Error('Den gemte publiceringshandling er ændret. Hent status igen.');
  this.config.storage.removeItem(this.key);this.attempt=undefined;
 }
 private async lookup(send:Send,attempt:PersistedIntent,epoch:number){
  const result=await send('getPublicationOperation',{resourceId:this.config.resourceId,operationId:(attempt.command.input as {operationId:string}).operationId});
  this.assertCurrent(epoch);
  if(result===null)return null;
  if(!record(result)||!('result' in result)||!('job' in result))throw new Error('Serverens svar på publiceringshandlingen kunne ikke bekræftes.');
  this.clear(attempt);return result as PublicationOperationReceipt;
 }
 async recover<TJob=unknown>(send:Send):Promise<PublicationOperationReceipt<TJob>|null>{
  if(this.running)return null;
  this.refresh();if(this.problem)throw new Error(this.problem);if(!this.attempt)return null;
  const attempt=this.attempt,epoch=this.epoch;this.running=true;
  try{return await this.lookup(send,attempt,epoch) as PublicationOperationReceipt<TJob>|null}finally{this.running=false}
 }
 async run<T>(send:Send,next?:PublicationCommand):Promise<T>{
  if(this.running)throw new Error('Handlingen afventer allerede svar.');
  this.refresh();if(this.problem)throw new Error(this.problem);
  if(this.attempt&&next)throw new Error('Afklar først den handling, der afventer svar.');
  const recovering=!!this.attempt;
  if(!this.attempt){
   if(!next)throw new Error('Der er ingen handling at gentage.');
   // Normalize exactly as the JSON request does before retaining its immutable input.
   const command=checkedCommand(JSON.parse(JSON.stringify(next)),this.config.resourceId);
   const attempt:PersistedIntent={version:1,userId:this.config.userId,resourceId:this.config.resourceId,command};
   try{this.config.storage.setItem(this.key,JSON.stringify(attempt))}catch{throw new Error('Browseren kunne ikke gemme publiceringshandlingen. Intet er sendt. Gør plads i browserlageret og prøv igen.')}
   this.attempt=attempt;
  }
  const attempt=this.attempt,epoch=this.epoch;let sent=false;this.running=true;
  try{
   if(recovering){const known=await this.lookup(send,attempt,epoch);if(known)return known.result as T}
   this.assertCurrent(epoch);sent=true;
   const result=await send(attempt.command.operation,structuredClone(attempt.command.input));
   this.assertCurrent(epoch);this.clear(attempt);return result as T;
  }catch(error){
   if(sent&&epoch===this.epoch&&definiteRejections.has((error as {code?:string})?.code??''))this.clear(attempt);
   throw error;
  }finally{this.running=false}
 }
}
